// src/lib/lead/phones.ts
// Normalize phone strings to just digits for comparison
export function normalizePhoneDigits(raw: string | null | undefined): string {
  if (!raw) return "";
  return raw.replace(/[^\d]/g, "");
}

/** Up to 10 digits (e.g. for profile / auth phone fields). */
export function phoneDigitsUpTo10(input: string | null | undefined): string {
  return normalizePhoneDigits(input).slice(0, 10);
}

export function formatPhoneStrict(raw: string | null | undefined): string | null {
  const d = normalizePhoneDigits(raw);

  // Reject absurd/invalid lengths
  if (d.length === 10) {
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  }

  if (d.length === 7) {
    return `${d.slice(0, 3)}-${d.slice(3)}`;
  }

  return null;
}

// If Whisper produces a runaway digit string (common with phone numbers),
// prevent it from polluting the transcript.
export function sanitizeWhisperChunk(text: string): string {
  if (!text) return text;

  let t = text.trim();

  // Remove Whisper silence markers
  t = t.replace(/\[BLANK_AUDIO\]/gi, "").trim();

  // Replace any contiguous digit run >= 20 with a short placeholder.
  // (8282828282... runaway becomes "[digits omitted]")
  t = t.replace(/\d{20,}/g, "[digits omitted]");
  t = t.replace(/^>>\s*/, "").trim();

  return t;
}

// Pull out all phone-like patterns from a transcript
export function extractCandidatePhonesFromTranscript(text: string): string[] {
  const candidates = new Set<string>();
  const pattern =
    /(?:\+?1[-.\s]?)?(?:\(\d{3}\)|\d{3})[-.\s]?\d{3}[-.\s]?\d{4}/g;

  let match: RegExpExecArray | null;
  while ((match = pattern.exec(text)) !== null) {
    candidates.add(match[0]);
  }

  return Array.from(candidates);
}

// Decide what the customer phone should be, based on extracted value + transcript
export function sanitizeCustomerPhone(
  extractedPhone: string | null | undefined,
  transcript: string,
  internalPhoneDigits: Set<string> = new Set()
): string | null {
  const extractedNorm = normalizePhoneDigits(extractedPhone);

  // STRICT: reject absurdly long digit runs (Whisper runaway protection)
  if (extractedNorm.length > 15) {
    return null;
  }

  const candidates = extractCandidatePhonesFromTranscript(transcript);

  // Filter out any phone that matches a known lot/office/BDC phone
  const nonInternal = candidates.filter(
    (num) => !internalPhoneDigits.has(normalizePhoneDigits(num))
  );

  // If the extracted phone is actually one of OUR numbers, try to replace it
  if (extractedNorm && internalPhoneDigits.has(extractedNorm)) {
    if (nonInternal.length === 1) {
      return formatPhoneStrict(nonInternal[0]);
    }
    return null;
  }

  // If model failed to extract a phone but we have exactly one non-internal candidate,
  // assume that's the customer's number.
  if (!extractedNorm && nonInternal.length === 1) {
    return formatPhoneStrict(nonInternal[0]);
  }

  // Otherwise keep whatever the model said, but STRICT-format it (or null)
  return formatPhoneStrict(extractedPhone);
}
